import React, { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "../App";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Skeleton } from "./ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Users, Plus, Edit, Trash2, Mail, Phone as PhoneIcon, Search, Lock, CheckCircle, XCircle, Shield, UserCog, Eye, Crown, History, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function SiteManagerMaintenance() {
  const { user } = useContext(AuthContext);
  const [managers, setManagers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [roleHistory, setRoleHistory] = useState([]);
  const [roleHistoryLoading, setRoleHistoryLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const [newManager, setNewManager] = useState({
    username: "",
    email: "",
    password: "",
    role: "site_manager",
    assigned_sites: []
  });

  const [editManager, setEditManager] = useState({
    username: "",
    email: "",
    role: "",
    position: "",
    assigned_sites: []
  });

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    loadManagers();
  }, [showDeactivated]); // eslint-disable-line

  const loadSites = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/sites`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      }
    } catch (error) {
      console.error("Error loading sites:", error);
    }
  };

  const loadManagers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/users/site-managers?include_inactive=${showDeactivated}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setManagers(data);
      } else {
        toast.error("Failed to load site managers");
      }
    } catch (error) {
      console.error("Error loading managers:", error);
      toast.error("Failed to load site managers");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateManager = async (e) => {
    e.preventDefault();
    
    if (!newManager.username.trim() || !newManager.email.trim() || !newManager.password.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newManager.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(newManager)
      });

      if (response.ok) {
        toast.success("Site manager created successfully!");
        setShowCreateDialog(false);
        setNewManager({ username: "", email: "", password: "", role: "site_manager", assigned_sites: [] });
        loadManagers();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to create site manager");
      }
    } catch (error) {
      console.error("Error creating manager:", error);
      toast.error("Failed to create site manager");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditManager = (manager) => {
    setSelectedManager(manager);
    setEditManager({
      username: manager.username,
      email: manager.email,
      role: manager.role,
      position: manager.position || "",
      assigned_sites: manager.assigned_sites || []
    });
    setShowEditDialog(true);
  };

  const loadRoleHistory = async () => {
    if (!selectedManager) return;
    setRoleHistoryLoading(true);
    setRoleHistory([]);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/users/${selectedManager.id}/role-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRoleHistory((data.role_history || []).slice().reverse());
      }
    } catch (e) {
      // silent
    } finally {
      setRoleHistoryLoading(false);
    }
  };

  const handleUpdateManager = async (e) => {
    e.preventDefault();
    
    if (!editManager.username.trim() || !editManager.email.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Extra confirmation when promoting/demoting to/from Admin or Company Owner
    const sensitive = ["admin", "company_owner"];
    const oldRole = selectedManager?.role;
    const newRole = editManager.role;
    if (newRole !== oldRole && (sensitive.includes(newRole) || sensitive.includes(oldRole))) {
      const label = (r) => (r === "company_owner" ? "Company Owner" : r === "admin" ? "Administrator" : r?.replace("_", " "));
      const ok = window.confirm(
        `You are about to change ${selectedManager?.username}'s role from "${label(oldRole)}" to "${label(newRole)}".\n\nThis grants or revokes high-level privileges. Continue?`
      );
      if (!ok) return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/users/${selectedManager.id}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(editManager)
      });

      if (response.ok) {
        toast.success("User updated successfully!");
        setShowEditDialog(false);
        setSelectedManager(null);
        setEditManager({ username: "", email: "", role: "", position: "", assigned_sites: [] });
        loadManagers();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to update user");
      }
    } catch (error) {
      console.error("Error updating manager:", error);
      toast.error("Failed to update site manager");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteManager = async () => {
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/users/${selectedManager.id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Site manager deactivated successfully!");
        setShowDeleteDialog(false);
        setSelectedManager(null);
        loadManagers();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to deactivate site manager");
      }
    } catch (error) {
      console.error("Error deactivating manager:", error);
      toast.error("Failed to deactivate site manager");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim() || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/users/${selectedManager.id}/reset-password`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ new_password: newPassword })
      });

      if (response.ok) {
        toast.success("Password reset successfully!");
        setShowPasswordDialog(false);
        setNewPassword("");
        setSelectedManager(null);
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to reset password");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error("Failed to reset password");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (manager) => {
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const endpoint = manager.is_active ? "deactivate" : "activate";
      const url = manager.is_active 
        ? `${API}/users/${manager.id}`
        : `${API}/users/${manager.id}/activate`;
      const method = manager.is_active ? "DELETE" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`User ${manager.is_active ? 'deactivated' : 'activated'} successfully!`);
        loadManagers();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || `Failed to ${endpoint} user`);
      }
    } catch (error) {
      console.error("Error toggling user status:", error);
      toast.error("Failed to update user status");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredManagers = managers.filter(manager =>
    manager.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    manager.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSiteNames = (siteIds) => {
    if (!siteIds || siteIds.length === 0) return "No sites assigned";
    const names = siteIds.map(id => {
      const site = sites.find(s => s.id === id);
      return site ? site.name : null;
    }).filter(Boolean);
    return names.length > 0 ? names.join(", ") : "No sites assigned";
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
              <Users className="w-8 h-8" />
              User Management
            </h1>
            <p className="text-slate-600 mt-1">
              Manage users, roles, and site access
            </p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-slate-700 hover:bg-slate-800"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New User
          </Button>
        </div>

        {/* Search Bar */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2" data-testid="show-deactivated-toggle">
            <Switch
              id="show-deactivated"
              checked={showDeactivated}
              onCheckedChange={setShowDeactivated}
              data-testid="show-deactivated-switch"
            />
            <Label htmlFor="show-deactivated" className="text-sm text-slate-600 cursor-pointer whitespace-nowrap">
              Show deactivated
            </Label>
          </div>
        </div>
      </div>

      {/* Managers Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-100 border-b">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">Name</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Email</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Role</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Status</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Assigned Sites</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Created</th>
                  <th className="text-right p-4 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredManagers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center p-8 text-slate-500">
                      {searchTerm ? "No managers found matching your search" : "No site managers found"}
                    </td>
                  </tr>
                ) : (
                  filteredManagers.map((manager) => {
                    const isInactive = manager.is_active === false;
                    return (
                    <tr key={manager.id} className={`border-b hover:bg-slate-50 ${isInactive ? "bg-slate-50/60 opacity-70" : ""}`} data-testid={`user-row-${manager.id}`}>
                      <td className="p-4">
                        <div className="font-medium text-slate-800">{manager.username}</div>
                        {manager.position && (
                          <div className="text-xs text-slate-500" data-testid={`user-position-${manager.id}`}>{manager.position}</div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4" />
                          {manager.email}
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge
                          variant={manager.role === "admin" ? "destructive" : manager.role === "view_only" ? "default" : "secondary"}
                          className={manager.role === "admin" ? "bg-red-600" : manager.role === "view_only" ? "bg-green-600" : ""}
                        >
                          {manager.role === "admin" ? (
                            <div className="flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              Administrator
                            </div>
                          ) : manager.role === "site_manager" ? (
                            <div className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              Site Manager
                            </div>
                          ) : manager.role === "view_only" ? (
                            <div className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              View Only
                            </div>
                          ) : manager.role === "supervisor" ? (
                            <div className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              Supervisor
                            </div>
                          ) : (
                            manager.role.replace('_', ' ')
                          )}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge variant={manager.is_active !== false ? "default" : "destructive"}>
                          {manager.is_active !== false ? "Active" : "Inactive"}
                        </Badge>
                        {isInactive && manager.deactivated_at && (
                          <div className="text-xs text-slate-400 mt-1" data-testid={`deactivated-label-${manager.id}`}>
                            Deactivated {new Date(manager.deactivated_at).toLocaleDateString("en-AU")}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="text-slate-600 text-sm">
                          {getSiteNames(manager.assigned_sites)}
                        </div>
                      </td>
                      <td className="p-4 text-slate-600">
                        {new Date(manager.created_at).toLocaleDateString("en-AU")}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditManager(manager)}
                            title="Edit user"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedManager(manager);
                              setShowPasswordDialog(true);
                            }}
                            title="Reset password"
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Lock className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleActive(manager)}
                            title={manager.is_active !== false ? "Deactivate user" : "Activate user"}
                            className={manager.is_active !== false ? "text-orange-600 hover:text-orange-700" : "text-green-600 hover:text-green-700"}
                          >
                            {manager.is_active !== false ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedManager(manager);
                              setShowDeleteDialog(true);
                            }}
                            title="Permanently deactivate"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Manager Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateManager}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="username">Full Name *</Label>
                <Input
                  id="username"
                  placeholder="e.g., John Smith"
                  value={newManager.username}
                  onChange={(e) => setNewManager(prev => ({ ...prev, username: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="e.g., john@construction.com"
                  value={newManager.email}
                  onChange={(e) => setNewManager(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={newManager.password}
                  onChange={(e) => setNewManager(prev => ({ ...prev, password: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <Select
                  value={newManager.role}
                  onValueChange={(value) => setNewManager(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger id="role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {user?.role === "admin" && (
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-red-600" />
                        <span>Administrator</span>
                      </div>
                    </SelectItem>
                    )}
                    <SelectItem value="site_manager">
                      <div className="flex items-center gap-2">
                        <UserCog className="w-4 h-4 text-blue-600" />
                        <span>Site Manager</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="view_only">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-green-600" />
                        <span>View Only</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="supervisor">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-600" />
                        <span>Supervisor</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Administrators have full system access
                </p>
              </div>
              <div className="space-y-2">
                <Label>Assign Sites</Label>
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  {sites.length === 0 ? (
                    <p className="text-sm text-slate-500">No sites available</p>
                  ) : (
                    sites.map((site) => (
                      <div key={site.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`new-site-${site.id}`}
                          checked={newManager.assigned_sites.includes(site.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewManager(prev => ({
                                ...prev,
                                assigned_sites: [...prev.assigned_sites, site.id]
                              }));
                            } else {
                              setNewManager(prev => ({
                                ...prev,
                                assigned_sites: prev.assigned_sites.filter(id => id !== site.id)
                              }));
                            }
                          }}
                          className="rounded border-slate-300"
                        />
                        <label
                          htmlFor={`new-site-${site.id}`}
                          className="text-sm cursor-pointer flex-1"
                        >
                          {site.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Select which sites this manager can access
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-700 hover:bg-slate-800">
                {isSubmitting ? "Creating..." : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Manager Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateManager}>
            <div className="space-y-4 py-4">
              {selectedManager?.account_name && (
                <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="edit-user-company">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <span className="text-xs text-slate-500">Company:</span>
                  <span className="text-sm font-medium text-slate-800">{selectedManager.account_name}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="edit-username">Full Name *</Label>
                <Input
                  id="edit-username"
                  placeholder="e.g., John Smith"
                  value={editManager.username}
                  onChange={(e) => setEditManager(prev => ({ ...prev, username: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email Address *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  placeholder="e.g., john@construction.com"
                  value={editManager.email}
                  onChange={(e) => setEditManager(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-role">Role *</Label>
                  <Popover onOpenChange={(open) => { if (open) loadRoleHistory(); }}>
                    <PopoverTrigger asChild>
                      <button type="button" className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="role-history-trigger">
                        <History className="w-3.5 h-3.5" /> Role history
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-80" data-testid="role-history-popover">
                      <div className="text-sm font-semibold text-slate-800 mb-2">Role change history</div>
                      {roleHistoryLoading ? (
                        <p className="text-sm text-slate-500">Loading…</p>
                      ) : roleHistory.length === 0 ? (
                        <p className="text-sm text-slate-500" data-testid="role-history-empty">No role changes recorded yet.</p>
                      ) : (
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {roleHistory.map((h, i) => (
                            <div key={i} className="text-sm border-l-2 border-amber-300 pl-3" data-testid={`role-history-entry-${i}`}>
                              <div className="text-slate-800">
                                <span className="capitalize">{(h.from || "—").replace("_", " ")}</span>
                                {" → "}
                                <span className="font-medium capitalize">{(h.to || "").replace("_", " ")}</span>
                              </div>
                              <div className="text-xs text-slate-500">
                                by {h.by_name || "Unknown"} · {h.at ? new Date(h.at).toLocaleString("en-AU") : ""}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
                <Select
                  value={editManager.role}
                  onValueChange={(value) => setEditManager(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger id="edit-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {user?.role === "admin" && (
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-red-600" />
                        <span>Administrator</span>
                      </div>
                    </SelectItem>
                    )}
                    {user?.role === "admin" && (
                    <SelectItem value="company_owner">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-amber-600" />
                        <span>Company Owner</span>
                      </div>
                    </SelectItem>
                    )}
                    <SelectItem value="site_manager">
                      <div className="flex items-center gap-2">
                        <UserCog className="w-4 h-4 text-blue-600" />
                        <span>Site Manager</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="view_only">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-green-600" />
                        <span>View Only</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="supervisor">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-600" />
                        <span>Supervisor</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Change user role to grant or revoke privileges
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-position">Position / Title</Label>
                <Input
                  id="edit-position"
                  data-testid="edit-position-input"
                  placeholder="e.g., Site Foreman, Project Manager"
                  value={editManager.position}
                  onChange={(e) => setEditManager(prev => ({ ...prev, position: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Assign Sites</Label>
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  {sites.length === 0 ? (
                    <p className="text-sm text-slate-500">No sites available</p>
                  ) : (
                    sites.map((site) => (
                      <div key={site.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`edit-site-${site.id}`}
                          checked={editManager.assigned_sites.includes(site.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditManager(prev => ({
                                ...prev,
                                assigned_sites: [...prev.assigned_sites, site.id]
                              }));
                            } else {
                              setEditManager(prev => ({
                                ...prev,
                                assigned_sites: prev.assigned_sites.filter(id => id !== site.id)
                              }));
                            }
                          }}
                          className="rounded border-slate-300"
                        />
                        <label
                          htmlFor={`edit-site-${site.id}`}
                          className="text-sm cursor-pointer flex-1"
                        >
                          {site.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Select which sites this manager can access
                </p>
              </div>
              <p className="text-xs text-slate-500">
                Note: Use the Reset Password button to change the password.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-700 hover:bg-slate-800">
                {isSubmitting ? "Updating..." : "Update Manager"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Deactivate Site Manager</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate <strong>{selectedManager?.username}</strong>?
              <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> This will mark the user as inactive. They will no longer be able to log in. You can reactivate them later.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteManager}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSubmitting ? "Deactivating..." : "Deactivate User"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                Resetting password for: <strong>{selectedManager?.username}</strong>
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password *</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Minimum 6 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
              />
            </div>
            <p className="text-xs text-slate-500">
              The user will need to use this new password on their next login.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowPasswordDialog(false);
                setNewPassword("");
                setSelectedManager(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleResetPassword}
              disabled={isSubmitting || !newPassword.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? "Resetting..." : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
